import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import API, { csrftoken } from '../services/api';
import { AI_SERVICE_BASE_URL } from '../config';

const api = new API();

let aiToken = null;
let cachedQueryString = null; // Track which query params the token was fetched with

const AI_TOKEN_EXCHANGE = `${window.location.origin}/ai/auth/token`; // Django endpoint

const getAIToken = async () => {
    // Only check RELEVANT query parameters (impersonation) - ignore navigation params
    // Navigation params like ?tab=reasoning&section=step_0 should not trigger token refresh
    const getRelevantParams = queryString => {
        const params = new URLSearchParams(queryString);
        const relevantParams = new URLSearchParams();

        // Only track impersonation-related parameters
        if (params.has('user')) {
            relevantParams.set('user', params.get('user'));
        }
        if (params.has('customer_id')) {
            relevantParams.set('customer_id', params.get('customer_id'));
        }

        return relevantParams.toString();
    };

    const currentQueryString = window.location.search;
    const currentRelevantParams = getRelevantParams(currentQueryString);
    const cachedRelevantParams = getRelevantParams(cachedQueryString || '');

    if (aiToken && cachedRelevantParams !== currentRelevantParams) {
        // Impersonation parameters changed, clear the cached token to refetch with new params
        this.clearAIToken();
    }

    if (aiToken) {
        return aiToken; // Return cached token
    }

    try {
        // Include current URL query parameters to support impersonation
        // This ensures ?user=email@domain.com is passed to the token exchange endpoint
        const tokenExchangeUrl = currentQueryString ? `${AI_TOKEN_EXCHANGE}${currentQueryString}` : AI_TOKEN_EXCHANGE;

        const response = await fetch(AI_TOKEN_EXCHANGE, {
            method: 'POST',
            credentials: 'include', // Include Django session cookies
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRFToken': csrftoken,
            },
            body: JSON.stringify(Object.fromEntries(new URLSearchParams(currentQueryString))),
        });

        if (response.ok) {
            const data = await response.json();
            aiToken = data.access_token; // Updated to match OAuth2 standard
            cachedQueryString = currentQueryString; // Remember which params this token was fetched with
            return aiToken;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }
};

/**
 * Custom hook for AI authentication and direct FastAPI communication.
 * Handles token exchange and refreshes when URL parameters change (e.g., impersonation).
 * Uses React Router's location to detect URL changes without polling.
 */
export const useAIAuth = () => {
    const location = useLocation();
    const [token, setToken] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMountedRef = useRef(true);

    // Extract ONLY impersonation-related query parameters
    // Navigation params like ?tab=reasoning&section=step_0 (after hash) should not trigger re-authentication
    // NOTE: In hash router, impersonation params are BEFORE the hash (window.location.search)
    // while React Router location.search only sees params AFTER the hash
    const relevantParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const relevantParams = new URLSearchParams();

        // Only track impersonation-related parameters (these are before the hash)
        if (params.has('user')) {
            relevantParams.set('user', params.get('user'));
        }
        if (params.has('customer_id')) {
            relevantParams.set('customer_id', params.get('customer_id'));
        }

        return relevantParams.toString();
    }, [location.pathname]); // Re-compute when pathname changes (page navigation), not on tab/section changes

    // Token exchange - re-runs only when RELEVANT URL parameters change (impersonation)
    // Navigation params like tab/section are ignored to prevent unnecessary re-authentication
    useEffect(() => {
        console.log('[useAIAuth] Authentication useEffect triggered');
        console.log('[useAIAuth] relevantParams:', relevantParams);
        console.log('[useAIAuth] location.pathname:', location.pathname);

        const authenticate = async () => {
            try {
                if (isMountedRef.current) {
                    setIsLoading(true);
                    setError(null);
                }

                const aiToken = await getAIToken();

                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    if (aiToken) {
                        console.log('[useAIAuth] Token obtained successfully');
                        setToken(aiToken);
                        setIsAuthenticated(true);
                    } else {
                        console.error('[useAIAuth] Failed to get AI token');
                        setError('Failed to get AI token');
                    }
                }
            } catch (err) {
                console.error('[useAIAuth] Authentication error:', err);
                if (isMountedRef.current) {
                    setError(err.message);
                }
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        authenticate();

        // Cleanup function to track component unmount
        return () => {
            isMountedRef.current = false;
        };
    }, [relevantParams]); // Only re-authenticate when impersonation params change, not navigation params

    // Authenticated fetch for FastAPI endpoints
    const aiFetch = useCallback(
        async (url, options = {}) => {
            if (!token) {
                throw new Error('No authentication token available');
            }

            // Ensure full URL for FastAPI
            const fastApiUrl = url.startsWith('http')
                ? url
                : `${AI_SERVICE_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

            return api.getJson(fastApiUrl, {
                method: options.method || 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...options.headers,
                },
                body: options.body,
                fixErr: true, // Reject with response data on error
            });
        },
        [token]
    );

    // Create a run request (replaces WebSocket connection)
    const createRunRequest = useCallback(
        async (userQuery, requestId = null) => {
            if (!token) {
                throw new Error('No authentication token available');
            }

            const fastApiUrl = `${AI_SERVICE_BASE_URL}/ai/analytics/runs`;

            return api.getJson(fastApiUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: {
                    user_query: userQuery,
                    request_id: requestId,
                },
                fixErr: true,
            });
        },
        [token]
    );

    // Cancel a run
    const cancelRun = useCallback(
        async runId => {
            if (!token) {
                throw new Error('No authentication token available');
            }

            const fastApiUrl = `${AI_SERVICE_BASE_URL}/ai/analytics/runs/${encodeURIComponent(runId)}/cancel`;

            return api.getJson(fastApiUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                fixErr: true,
            });
        },
        [token]
    );

    return {
        token,
        isAuthenticated,
        isLoading,
        error,
        aiFetch, // Use this for all FastAPI REST calls
        createRunRequest, // Use this to create new runs
        cancelRun, // Use this to cancel active runs
    };
};

export default useAIAuth;
